import { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { explorerTxUrl } from '../network';

const PAGE_SIZE = 100;

function displayPoolName(addr) {
    return addr.replace(/^Pool: /, '').replace(/^(State|City|Industry): /, '');
}

function formatAmount(tx) {
    const val = parseFloat(tx.usd_value ?? tx.amount ?? 0);
    return `$${val.toFixed(val < 0.01 ? 6 : 2)} USDC`;
}

const TYPE_ALL = 'All';
const TYPE_DONATIONS = 'Contributions';
const TYPE_DISTRIBUTIONS = 'Distributions';

function LedgerHistory() {
    const [txs, setTxs]         = useState([]);
    const [total, setTotal]     = useState(0);
    const [offset, setOffset]   = useState(0);
    const [loading, setLoading] = useState(true);
    const [search, setSearch]   = useState('');
    const [typeFilter, setType] = useState(TYPE_ALL);

    const fetchPage = useCallback(async (off) => {
        setLoading(true);
        try {
            const res = await api.get(`/admin/transactions?limit=${PAGE_SIZE}&offset=${off}`);
            setTxs(res.data.rows);
            setTotal(res.data.total);
            setOffset(off);
        } catch (err) {
            console.error('Failed to fetch ledger', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchPage(0); }, [fetchPage]);

    const filtered = txs.filter(tx => {
        const isDonation = tx.recipient_address?.startsWith('Pool: ');
        if (typeFilter === TYPE_DONATIONS && !isDonation) return false;
        if (typeFilter === TYPE_DISTRIBUTIONS && isDonation) return false;

        const q = search.toLowerCase();
        if (!q) return true;
        return (
            (tx.user_name  || '').toLowerCase().includes(q) ||
            (tx.user_email || '').toLowerCase().includes(q) ||
            (tx.recipient_address || '').toLowerCase().includes(q) ||
            (tx.tx_hash || '').toLowerCase().includes(q) ||
            String(parseFloat(tx.usd_value ?? tx.amount ?? 0).toFixed(2)).includes(q)
        );
    });

    const tabStyle = (active) => ({
        padding: '8px 18px', borderRadius: '20px', border: '1px solid',
        borderColor: active ? 'rgba(255,193,7,0.6)' : 'rgba(255,255,255,0.12)',
        background: active ? 'rgba(255,193,7,0.15)' : 'rgba(255,255,255,0.05)',
        color: active ? '#FFC107' : 'rgba(255,255,255,0.6)',
        fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem', transition: 'all 0.2s ease',
    });

    const thStyle = {
        textAlign: 'left', padding: '10px 14px', fontSize: '0.78rem', fontWeight: 700,
        textTransform: 'uppercase', letterSpacing: '0.05em', color: 'rgba(255,255,255,0.4)',
        borderBottom: '1px solid rgba(255,255,255,0.08)', whiteSpace: 'nowrap',
    };

    const tdStyle = {
        padding: '11px 14px', borderBottom: '1px solid rgba(255,255,255,0.05)',
        fontSize: '0.875rem', verticalAlign: 'middle',
    };

    const btnStyle = (disabled) => ({
        padding: '8px 20px', borderRadius: '10px', fontWeight: 600, fontSize: '0.88rem',
        border: '1px solid rgba(255,193,7,0.3)',
        background: disabled ? 'rgba(255,255,255,0.04)' : 'rgba(255,193,7,0.12)',
        color: disabled ? 'rgba(255,255,255,0.2)' : '#ffc107',
        cursor: disabled ? 'not-allowed' : 'pointer',
    });

    const hasPrev = offset > 0;
    const hasNext = offset + PAGE_SIZE < total;
    const page    = Math.floor(offset / PAGE_SIZE) + 1;
    const pages   = Math.ceil(total / PAGE_SIZE);

    return (
        <div style={{
            minHeight: '100vh',
            background: 'linear-gradient(180deg, #12121a 0%, #1a1a2e 50%, #12121a 100%)',
            color: '#ffffff', position: 'relative', overflow: 'hidden',
        }}>
            <div style={{ position: 'fixed', top: '-20%', right: '-10%', width: '500px', height: '500px', background: 'radial-gradient(circle, rgba(255,193,7,0.1) 0%, transparent 70%)', borderRadius: '50%', pointerEvents: 'none', zIndex: 0 }} />
            <div style={{ position: 'fixed', bottom: '-30%', left: '-10%', width: '400px', height: '400px', background: 'radial-gradient(circle, rgba(255,193,7,0.06) 0%, transparent 70%)', borderRadius: '50%', pointerEvents: 'none', zIndex: 0 }} />

            <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '40px 20px', position: 'relative', zIndex: 10 }}>
                <h1 style={{
                    marginBottom: '6px', fontSize: '1.8rem', fontWeight: 700,
                    background: 'linear-gradient(135deg, #ffffff 0%, rgba(255,255,255,0.7) 100%)',
                    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                }}>
                    📒 Full Ledger History
                </h1>
                <p style={{ color: 'rgba(255,255,255,0.45)', marginBottom: '28px', fontSize: '0.9rem' }}>
                    {loading ? 'Loading…' : `${total.toLocaleString()} total transactions — page ${page} of ${pages} (showing ${filtered.length})`}
                </p>

                {/* Filters */}
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '16px' }}>
                    {[TYPE_ALL, TYPE_DONATIONS, TYPE_DISTRIBUTIONS].map(t => (
                        <button key={t} onClick={() => setType(t)} style={tabStyle(typeFilter === t)}>
                            {t === TYPE_ALL ? '🔗 All' : t === TYPE_DONATIONS ? '🌱 Contributions' : '🚀 Distributions'}
                        </button>
                    ))}
                </div>

                <input
                    type="text"
                    placeholder="Search by name, email, pool, address, hash, or amount…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    style={{
                        maxWidth: '500px', width: '100%', marginBottom: '20px',
                        padding: '10px 14px', borderRadius: '10px',
                        border: '1px solid rgba(255,255,255,0.12)',
                        background: 'rgba(255,255,255,0.06)', color: '#ffffff',
                        fontSize: '0.95rem', boxSizing: 'border-box',
                        backdropFilter: 'blur(10px)', outline: 'none',
                    }}
                />

                {loading ? (
                    <p style={{ color: 'rgba(255,255,255,0.45)' }}>Loading transactions…</p>
                ) : (
                    <>
                        <div style={{
                            background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
                            borderRadius: '16px', backdropFilter: 'blur(20px)', overflowX: 'auto',
                        }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr>
                                        <th style={thStyle}>Type</th>
                                        <th style={thStyle}>User</th>
                                        <th style={thStyle}>Recipient / Hive</th>
                                        <th style={thStyle}>Amount (USDC)</th>
                                        <th style={thStyle}>Tx Hash</th>
                                        <th style={thStyle}>Status</th>
                                        <th style={thStyle}>Timestamp</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.length === 0 ? (
                                        <tr>
                                            <td colSpan={7} style={{ ...tdStyle, color: 'rgba(255,255,255,0.35)', textAlign: 'center', padding: '32px' }}>
                                                No transactions found.
                                            </td>
                                        </tr>
                                    ) : filtered.map(tx => {
                                        const isDonation = tx.recipient_address?.startsWith('Pool: ');
                                        const recipient  = isDonation
                                            ? displayPoolName(tx.recipient_address)
                                            : (tx.recipient_address || 'Unknown');
                                        const shortAddr  = !isDonation && recipient.length > 20
                                            ? recipient.slice(0, 8) + '…' + recipient.slice(-6)
                                            : recipient;
                                        return (
                                            <tr key={tx.id}
                                                style={{ transition: 'background 0.15s' }}
                                                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.04)'}
                                                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                            >
                                                <td style={tdStyle}>
                                                    <span style={{
                                                        display: 'inline-block', padding: '2px 10px', borderRadius: '12px',
                                                        fontSize: '0.75rem', fontWeight: 700,
                                                        background: isDonation ? 'rgba(40,200,100,0.15)' : 'rgba(99,179,255,0.12)',
                                                        color: isDonation ? '#4dd880' : '#63b3ff',
                                                    }}>
                                                        {isDonation ? '🌱 Contribution' : '🚀 Distribution'}
                                                    </span>
                                                </td>
                                                <td style={tdStyle}>
                                                    <div style={{ fontWeight: 600, color: '#fff' }}>{tx.user_name || '—'}</div>
                                                    {tx.user_email && <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)' }}>{tx.user_email}</div>}
                                                </td>
                                                <td style={tdStyle}>
                                                    <span style={{ fontFamily: isDonation ? 'inherit' : 'monospace', fontSize: isDonation ? '0.875rem' : '0.8rem', color: 'rgba(255,255,255,0.75)' }}>
                                                        {isDonation ? recipient : shortAddr}
                                                    </span>
                                                </td>
                                                <td style={{ ...tdStyle, fontWeight: 700, color: '#FFC107', whiteSpace: 'nowrap' }}>
                                                    {formatAmount(tx)}
                                                </td>
                                                <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '0.78rem', color: 'rgba(255,255,255,0.35)' }}>
                                                    {tx.tx_hash && tx.tx_hash.length > 20 ? (
                                                        <a href={explorerTxUrl(tx.tx_hash)} target="_blank" rel="noopener noreferrer"
                                                            style={{ color: '#ffc107', textDecoration: 'none', fontSize: '0.75rem' }}
                                                            title={tx.tx_hash}>
                                                            {tx.tx_hash.slice(0, 8)}…{tx.tx_hash.slice(-6)} ↗
                                                        </a>
                                                    ) : (tx.tx_hash || '—')}
                                                </td>
                                                <td style={tdStyle}>
                                                    <span style={{
                                                        display: 'inline-block', padding: '2px 8px', borderRadius: '10px',
                                                        fontSize: '0.72rem', fontWeight: 700,
                                                        background: tx.status === 'CONFIRMED' ? 'rgba(40,167,69,0.2)' : 'rgba(220,53,69,0.2)',
                                                        color: tx.status === 'CONFIRMED' ? '#4dd880' : '#ff6b6b',
                                                    }}>
                                                        {tx.status}
                                                    </span>
                                                </td>
                                                <td style={{ ...tdStyle, color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                                                    {new Date(tx.timestamp).toLocaleString()}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', marginTop: '20px' }}>
                            <button disabled={!hasPrev} onClick={() => fetchPage(offset - PAGE_SIZE)} style={btnStyle(!hasPrev)}>
                                ← Previous 100
                            </button>
                            <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem' }}>
                                Page {page} of {pages}
                            </span>
                            <button disabled={!hasNext} onClick={() => fetchPage(offset + PAGE_SIZE)} style={btnStyle(!hasNext)}>
                                Next 100 →
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default LedgerHistory;
